//XPRSuvey-App/src/Components/Survey/Pages/AssessmentManagement/AddQuestions.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  PlusCircle,
  Trash2,
  Copy,
  Layers,
  User,
  ListChecks,
  Circle,
  Square,
  Check,
  ChevronDown,
  FileText,
  GripVertical,
  Plus,
  Pencil,
  X,
  ToggleLeft,
  ToggleRight,
  Shuffle,
} from "lucide-react";
import {
  createSurveyQuestions,
  getSurveyData,
  getSurveyQuestions,
  assignChannelToAssessment,
  GetChannelsAccordingToAssessment,
} from "../../Services/SurveyService";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { motion, AnimatePresence } from "framer-motion";
import RichTextEditor from "../../../../Utility/RichTextEditor";
import SurveyNavTabs from "../../../../Utility/SurveyNavTabs";
import { sanitizeHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../../Utility/sanitizeHtml";
import { SidebarSkeleton, ContentSkeleton } from "../AddQuestionsSkeleton";
import { getChannels } from "../../Services/MasterDataService";

const DEFAULT_SECTIONS = ["Section A", "Section B"];
const SECTIONS_WITHOUT_QUALIFICATION = ["Section B"];
const SECTIONS_WITH_CASE_STUDY = ["Section B"];
const QUALIFICATION_OPTIONS = ["Experienced", "Fresher", "Both"];
const CHANNEL_TYPE_OPTIONS = ["Bancassurance", "Agency"];

const caseKey = (channel, caseName) => `${channel}::${caseName}`;

const describeQuestionScope = (question, allQuestions) => {
  const scopedQuestions = allQuestions.filter((other) => {
    if (other.Section !== question.Section || other.ChannelType !== question.ChannelType) return false;
    if (question.Section === "Section B") return other.CaseType === question.CaseType;
    return true;
  });
  const position = scopedQuestions.findIndex((other) => other.QuestionID === question.QuestionID) + 1;
  const parts = [question.ChannelType];
  if (question.Section === "Section B" && question.CaseType) parts.push(question.CaseType);
  parts.push(`Q${position}`);
  return parts.join(" · ");
};

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").trim();
};

const questionTypes = [
  "Short Answer",
  "Paragraph",
  "Multiple Choice",
  "Check Boxes",
  "Rank",
  "Dropdown",
  "Date",
  "Email",
  "Mobile Number",
  "Answer in Number",
  "Rating Bar",
];

const CaseTabs = ({ options, value, onSelect, onAdd, onRename, onRemove, viewOnly }) => {
  const [editingCase, setEditingCase] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const startEditing = (caseName) => { setEditingCase(caseName); setEditingValue(caseName); };
  const commitEditing = () => { if (editingCase) onRename(editingCase, editingValue); setEditingCase(null); };

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {options.map((option) => {
        const active = value === option; const isEditing = editingCase === option;
        return (
          <div key={option}
            className={`group flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all ${active
              ? "bg-white text-[#7f35b2] shadow-sm ring-1 ring-[#7f35b2]/20"
              : "bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
          >
            {isEditing ? (
              <input
                autoFocus
                value={editingValue}
                onChange={(event) => setEditingValue(event.target.value)}
                onBlur={commitEditing}
                onKeyDown={(event) => {
                  if (event.key === "Enter") commitEditing();
                  if (event.key === "Escape") setEditingCase(null);
                }}
                className="w-20 bg-transparent text-sm outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => onSelect(option)}
                aria-pressed={active}
                className="whitespace-nowrap"
              >
                {option}
              </button>
            )}

            {!isEditing && !viewOnly && (
              <span className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
                <button type="button" onClick={() => startEditing(option)} title="Rename case"
                  className="rounded p-0.5 hover:bg-black/5" >
                  <Pencil size={11} />
                </button>
                
                <button type="button" onClick={() => onRemove(option)} title="Remove case"
                  className="rounded p-0.5 hover:bg-red-100 hover:text-red-500">
                  <X size={11} />
                </button>
              </span>
            )}
          </div>
        );
      })}
      {!viewOnly && (
        <button
          type="button"
          onClick={onAdd}
          title="Add a new case"
          className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-400 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
        >
          <Plus size={14} />
          Add case
        </button>
      )}
    </div>
  );
};

const OptionIndicator = ({ optionType, index }) => {
  if (optionType === "Multiple Choice") {
    return <Circle size={16} className="flex-shrink-0 text-gray-300" aria-hidden="true" />;
  }
  if (optionType === "Check Boxes") {
    return <Square size={16} className="flex-shrink-0 text-gray-300" aria-hidden="true" />;
  }
  if (optionType === "Dropdown" || optionType === "Rank") {
    return (
      <span className="w-5 flex-shrink-0 text-center text-sm font-medium text-gray-400">
        {index + 1}.
      </span>
    );
  }
  return null;
};

const AddQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);

  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const channelNames = useMemo(
    () => channels.filter((c) => c.isactive).map((c) => c.value),
    [channels]
  );
  const channelIdMap = useMemo(() => channels.reduce((acc, c) => ({ ...acc, [c.value]: c.id }), {}), [channels]);

  const sections = DEFAULT_SECTIONS;
  const [selectedSection, setSelectedSection] = useState(DEFAULT_SECTIONS[0]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [expandedSection, setExpandedSection] = useState(DEFAULT_SECTIONS[0]);
  const [visibleChannels, setVisibleChannels] = useState([]);
  const [savedChannels, setSavedChannels] = useState([]);
  const [channelPickerOpen, setChannelPickerOpen] = useState(false);
  const [channelSaving, setChannelSaving] = useState(false);
  // const [channelRemoveMode, setChannelRemoveMode] = useState(false);

  const [channelCases, setChannelCases] = useState({});
  const [selectedCase, setSelectedCase] = useState(null);
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const [caseStudyContent, setCaseStudyContent] = useState({});
  const [channelInstructions, setChannelInstructions] = useState({});
  const [placementModal, setPlacementModal] = useState(null);

  const location = useLocation();
  const { surveyUID, surveyName, surveyDescription, viewOnly = false } = location.state || {};
  const questionRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    const fetchChannels = async () => {
      setChannelsLoading(true);
      try {
        const res = await getChannels();
        if (!cancelled && res?.data?.status === "success") {
          setChannels(res.data.channels ?? []);
        }
      } catch (error) {
        if (!cancelled) CustomToast("fail", "Failed to load channels. Please refresh and try again.");
      } finally {
        if (!cancelled) setChannelsLoading(false);
      }
    };
    fetchChannels();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (channels.length === 0) return;
    setChannelCases((previous) => {
      let changed = false;
      const next = { ...previous };
      channels.forEach((channel) => {
        if (!(channel.value in next)) {
          next[channel.value] = [];
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [channels]);

  // useEffect(() => {
  //   if (!selectedChannel && channelNames.length > 0) {
  //     setSelectedChannel(channelNames[0]);
  //   }
  // }, [channelNames, selectedChannel]);


  useEffect(() => {
    if (surveyUID || visibleChannels.length === 0 || questions.length > 0) return;
    setQuestions([
      {
        QuestionID: Date.now().toString().slice(-8),
        QuestionName: "",
        OptionType: "Multiple Choice",
        Options: [],
        IsRequired: true,
        IsJumbleOptions: false,
        Status: true,
        Section: DEFAULT_SECTIONS[0],
        Qualification: QUALIFICATION_OPTIONS[0],
        ChannelType: visibleChannels[0],
        CaseType: null,
      },
    ]);
  }, [surveyUID, visibleChannels]);

  useEffect(() => {
    if (surveyUID && !channelsLoading) {
      loadSurvey();
    }
  }, [surveyUID, channelsLoading]);

  useEffect(() => {
    const availableCases = channelCases[selectedChannel] || [];
    if (!availableCases.includes(selectedCase)) {
      setSelectedCase(availableCases[0] || null);
    }
  }, [selectedChannel, channelCases]);

  useEffect(() => {
    if (!pendingScrollId) return;
    const questionCard = questionRefs.current[pendingScrollId];
    if (questionCard) {
      questionCard.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setPendingScrollId(null);
    }
  }, [questions, pendingScrollId]);

  const loadSurvey = async () => {
      try {
        setLoading(true);
        const request = { surveyUID, surveyName, surveyDescription };
        const response = await getSurveyData(request);

        if (response.data.status.toLowerCase() === "success") {
          const surveyDetails = response.data.surveyDetails[0];
          setSurvey(surveyDetails);
        }

        // --- FETCH ASSIGNED CHANNELS FROM API ---
        let apiAssignedChannels = [];
        try {
          const assignedRes = await GetChannelsAccordingToAssessment(surveyUID);
          if (assignedRes?.data?.status?.toLowerCase() === "success") {
            // Adjust this line if the actual response shape differs from Swagger's
            // example — check the Response tab in Swagger for the real field name
            // (e.g. `channels`, `data`, or a plain array) and the shape of each item.
            const assignedChannels = assignedRes.data.channels || assignedRes.data.data || [];
            apiAssignedChannels = assignedChannels
              .map((c) => c.channelName || c.channel || c.name || c)
              .filter(Boolean);
          }
        } catch (err) {
          CustomToast("fail", err.response?.data?.message || err.message || "Failed to load assigned channels.");
        }



        const questionResponse = await getSurveyQuestions(request);

        if (questionResponse.data.status.toLowerCase() === "success") {
          const extractedCaseContent = { ...caseStudyContent };
          const extractedCasesByChannel = {};

          const loadedQuestions = questionResponse.data.surveyDetails.map(
            (question) => {
              const caseName = question.caseTitle || null;
              const channelName = question.channel || channelNames[0];

              if (question.section === "Section B" && caseName) {
                if (!extractedCasesByChannel[channelName]) {
                  extractedCasesByChannel[channelName] = new Set();
                }
                extractedCasesByChannel[channelName].add(caseName);

                const key = caseKey(channelName, caseName);
                if (question.caseDescription && !extractedCaseContent[key]) {
                  extractedCaseContent[key] = question.caseDescription;
                }
              }

              return {
                QuestionID: question.questionID,
                UniqueKey: question.uniqueKey,
                SurveyID: question.surveyID,
                QuestionName: question.questionName,
                OptionType: question.optionType,
                Options: (question.options || []).map((option) => ({
                  optionID: option.optionID,
                  optionName: option.optionName,
                  isDisqualifying: option.isDisqualifying ?? false,
                  rank: option.answerRank ?? "",
                })),
                IsRequired: question.isRequired === 1,
                IsJumbleOptions: question.isJumbleOptions === 1,
                Status: question.status === 1,
                QuestionSequence: question.questionSequence,
                Section: DEFAULT_SECTIONS.includes(question.section) ? question.section : DEFAULT_SECTIONS[0],
                Qualification: question.experienceType || QUALIFICATION_OPTIONS[0],
                ChannelType: channelName,
                CaseID: question.caseID,
                CaseSequence: question.caseSequence,
                CaseTitle: question.caseTitle,
                CaseDescription: question.caseDescription,
                CaseType: caseName,
              };
            },
          );
          loadedQuestions.sort((first, second) => first.QuestionSequence - second.QuestionSequence);
          setQuestions(loadedQuestions);

          // Assessments created before the channel-assignment feature existed
          // never went through assignChannelToAssessment, so
          // GetChannelsAccordingToAssessment can come back empty for them —
          // even though their saved questions already carry a real channel.
          // Fall back to whatever channel(s) the loaded questions actually
          // have, so previously-assigned data still displays correctly.
          const channelsFromQuestions = Array.from(
            new Set(loadedQuestions.map((question) => question.ChannelType).filter(Boolean))
          );
          const combinedChannels = Array.from(
            new Set([...apiAssignedChannels, ...channelsFromQuestions])
          );

          if (combinedChannels.length > 0) {
            setVisibleChannels(combinedChannels);
            setSavedChannels(combinedChannels);
            setSelectedChannel((current) =>
              current && combinedChannels.includes(current) ? current : combinedChannels[0]
            );
          }

          const extractedInstructions = {};

          loadedQuestions.forEach((question) => {
            if (
              question.Section === "Section A" &&
              question.ChannelType &&
              !extractedInstructions[question.ChannelType] &&
              question.CaseDescription
            ) {
              extractedInstructions[question.ChannelType] = question.CaseDescription;
            }
          });
          setChannelInstructions(extractedInstructions);

          const newChannelCases = {};
          channelNames.forEach((name) => {
            newChannelCases[name] = Array.from(extractedCasesByChannel[name] || []);
          });

          setChannelCases(newChannelCases);
          setCaseStudyContent(extractedCaseContent);
          const activeChannelCases = newChannelCases[selectedChannel] || [];
          if (!activeChannelCases.includes(selectedCase)) {
            setSelectedCase(activeChannelCases[0] || null);
          }
        }
      } catch (error) {
        CustomToast("fail", error.response?.data?.message || error.message || "Failed to load survey data.");
      }
      finally {
        setLoading(false);
      }
    };

  
  const scrollToQuestion = (questionID) => {
    const questionCard = questionRefs.current[questionID];
    if (questionCard) {
      questionCard.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSidebarItemClick = (section, questionID) => {
    if (section !== selectedSection) {
      setSelectedSection(section);
      setPendingScrollId(questionID);
    } else {
      scrollToQuestion(questionID);
    }
  };

  const toggleSection = (section) => {
    setSelectedSection(section);
    setExpandedSection((current) => (current === section ? null : section));
  };

  const addQuestion = (
    section = selectedSection || DEFAULT_SECTIONS[0],
    channel = selectedChannel || channelNames[0]
  ) => {
    // No API call happens here, so there's no backend message to show.
    // The button that triggers this is already disabled when no channel
    // is assigned — this check is just a silent safety net in case
    // addQuestion is ever called programmatically.
    if (!channel || !visibleChannels.includes(channel)) {
      return;
    }

    const availableCases = channelCases[channel] || [];

    if (section === "Section B" && availableCases.length === 0) {
      CustomToast("fail", "Add a case first before adding questions to Section B.");
      return;
    }

    const targetCase = section === "Section B"
      ? (availableCases.includes(selectedCase) ? selectedCase : availableCases[0])
      : null;

    const newQuestion = {
      QuestionID: Date.now().toString().slice(-8),
      QuestionName: "",
      OptionType: "Multiple Choice",
      Options: [],
      IsRequired: true,
      IsJumbleOptions: false,
      Status: true,
      Section: section,
      Qualification: QUALIFICATION_OPTIONS[0],
      ChannelType: channel,
      CaseType: targetCase,
    };

    setQuestions((previousQuestions) => [...previousQuestions, newQuestion]);
    setSelectedSection(section);
    setSelectedChannel(channel);
    setExpandedSection(section);
    if (targetCase) setSelectedCase(targetCase);
    setPendingScrollId(newQuestion.QuestionID);
  };

  const updateQuestion = (questionID, field, value) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question) =>
        question.QuestionID === questionID ? { ...question, [field]: value } : question,
      ),
    );
  };

  const requestSectionChange = (questionID, newSection) => {
    const question = questions.find((item) => item.QuestionID === questionID);
    if (!question || question.Section === newSection) return;

    const currentChannel = question.ChannelType || selectedChannel || channelNames[0];
    const casesForChannel = channelCases[currentChannel] || [];

    setPlacementModal({
      mode: "move",
      questionID,
      toSection: newSection,
      channel: currentChannel,
      caseType:
        newSection === "Section B" && casesForChannel.includes(question.CaseType)
          ? question.CaseType
          : null,
      addingNewCase: newSection === "Section B" && casesForChannel.length === 0,
      newCaseName: "",
    });
  };

  const requestDuplicate = (question) => {
    const targetChannel = question.ChannelType || selectedChannel || channelNames[0];
    const casesForChannel = channelCases[targetChannel] || [];

    setPlacementModal({
      mode: "duplicate",
      questionID: question.QuestionID,
      toSection: question.Section,
      channel: targetChannel,
      caseType:
        question.Section === "Section B" && casesForChannel.includes(question.CaseType)
          ? question.CaseType
          : null,
      addingNewCase: question.Section === "Section B" && casesForChannel.length === 0,
      newCaseName: "",
    });
  };

  const closePlacementModal = () => setPlacementModal(null);

  const confirmPlacement = () => {
    if (!placementModal) return;
    const { mode, questionID, toSection, channel, addingNewCase, newCaseName } = placementModal;
    let { caseType } = placementModal;

    if (toSection === "Section B") {
      if (addingNewCase) {
        const trimmedName = newCaseName.trim();
        if (!trimmedName) {
          CustomToast("fail", "Enter a name for the new case.");
          return;
        }
        const existingCases = channelCases[channel] || [];
        if (existingCases.includes(trimmedName)) {
          CustomToast("fail", `"${trimmedName}" already exists in ${channel}.`);
          return;
        }

        setChannelCases((previous) => ({
          ...previous,
          [channel]: [...(previous[channel] || []), trimmedName],
        }));
        setCaseStudyContent((previous) => ({
          ...previous,
          [caseKey(channel, trimmedName)]: "",
        }));
        caseType = trimmedName;
      }

      if (!caseType) {
        CustomToast("fail", "Select or add a case for Section B.");
        return;
      }
    } else {
      caseType = null;
    }

    if (mode === "duplicate") {
      const original = questions.find((item) => item.QuestionID === questionID);
      if (!original) {
        setPlacementModal(null);
        return;
      }

      const duplicate = {
        ...original,
        QuestionID: Date.now().toString().slice(-8),
        Options: original.Options.map((option) => ({ ...option })),
        Section: toSection,
        ChannelType: channel,
        CaseType: caseType,
      };

      setQuestions((previousQuestions) => [...previousQuestions, duplicate]);
      setPendingScrollId(duplicate.QuestionID);
    } else {
      setQuestions((previousQuestions) =>
        previousQuestions.map((question) =>
          question.QuestionID === questionID
            ? { ...question, Section: toSection, ChannelType: channel, CaseType: caseType }
            : question,
        ),
      );
    }

    // Follow the question to where it landed so it's immediately visible —
    // applies whether it was moved or duplicated.
    setSelectedSection(toSection);
    setSelectedChannel(channel);
    setExpandedSection(toSection);
    if (caseType) setSelectedCase(caseType);

    setPlacementModal(null);
  };

  const addOption = (questionID) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question) =>
        question.QuestionID === questionID
          ? {
            ...question,
            Options: [...question.Options, { optionName: "" }],
          }
          : question,
      ),
    );
  };

  const addOtherOption = (questionID) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question) =>
        question.QuestionID === questionID
          ? {
            ...question,
            Options: [...question.Options, { optionName: "Other" }],
          }
          : question,
      ),
    );
  };

  const updateOption = (questionID, index, value) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question) => {
        if (question.QuestionID !== questionID) return question;
        const updatedOptions = [...question.Options];
        // Preserve other option fields (e.g. `rank`) instead of replacing
        // the whole object — otherwise editing the label after setting a
        // rank would silently wipe it out.
        updatedOptions[index] = { ...updatedOptions[index], optionName: value };
        return { ...question, Options: updatedOptions };
      }),
    );
  };

  // Rank-type only: the correct position (1-based) this option should
  // occupy when the respondent orders all options correctly.
  const updateOptionRank = (questionID, index, value) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question) => {
        if (question.QuestionID !== questionID) return question;
        const updatedOptions = [...question.Options];
        updatedOptions[index] = { ...updatedOptions[index], rank: value };
        return { ...question, Options: updatedOptions };
      }),
    );
  };

  const deleteOption = (questionID, index) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question) =>
        question.QuestionID === questionID
          ? {
            ...question,
            Options: question.Options.filter((_, optionIndex) => optionIndex !== index),
          }
          : question,
      ),
    );
  };

  const deleteQuestion = (questionID) => {
    const question = questions.find((item) => item.QuestionID === questionID);
    if (question && Number.isInteger(question.QuestionID)) {
      setDeletedQuestionIds((previousIds) => [...previousIds, question.QuestionID]);
    }
    delete questionRefs.current[questionID];
    setQuestions((previousQuestions) =>
      previousQuestions.filter((item) => item.QuestionID !== questionID),
    );
  };

  const addCase = () => {
    const currentList = channelCases[selectedChannel] || [];
    let counter = currentList.length + 1;
    let newCaseName = `Case ${counter}`;

    while (currentList.includes(newCaseName)) {
      counter += 1;
      newCaseName = `Case ${counter}`;
    }

    setChannelCases((prev) => ({
      ...prev,
      [selectedChannel]: [...(prev[selectedChannel] || []), newCaseName],
    }));

    setCaseStudyContent((previousContent) => ({
      ...previousContent,
      [caseKey(selectedChannel, newCaseName)]: "",
    }));
    setSelectedCase(newCaseName);
  };

  const renameCase = (oldName, rawNewName) => {
    const newName = rawNewName.trim();
    if (!newName || newName === oldName) return;

    const currentList = channelCases[selectedChannel] || [];
    if (currentList.includes(newName)) {
      CustomToast("fail", `"${newName}" already exists in this channel.`);
      return;
    }

    setChannelCases((prev) => ({
      ...prev,
      [selectedChannel]: (prev[selectedChannel] || []).map((name) =>
        name === oldName ? newName : name
      ),
    }));

    setCaseStudyContent((previousContent) => {
      const oldKey = caseKey(selectedChannel, oldName);
      const newKey = caseKey(selectedChannel, newName);
      const { [oldKey]: content, ...rest } = previousContent;
      return { ...rest, [newKey]: content ?? "" };
    });

    setQuestions((previousQuestions) =>
      previousQuestions.map((question) =>
        question.Section === "Section B" &&
          question.ChannelType === selectedChannel &&
          question.CaseType === oldName
          ? { ...question, CaseType: newName }
          : question,
      ),
    );

    setSelectedCase((current) => (current === oldName ? newName : current));
  };

  const removeCase = (caseName) => {
    const currentList = channelCases[selectedChannel] || [];

    const questionsInCase = questions.filter(
      (question) =>
        question.Section === "Section B" &&
        question.ChannelType === selectedChannel &&
        question.CaseType === caseName,
    );

    if (questionsInCase.length > 0) {
      CustomToast("fail", `"${caseName}" still has ${questionsInCase.length} question(s). Move or delete them before removing this case.`);
      return;
    }

    const remainingCases = currentList.filter((name) => name !== caseName);
    setChannelCases((prev) => ({
      ...prev,
      [selectedChannel]: remainingCases,
    }));

    setCaseStudyContent((previousContent) => {
      const { [caseKey(selectedChannel, caseName)]: _removed, ...rest } = previousContent;
      return rest;
    });

    setSelectedCase((current) => (current === caseName ? (remainingCases[0] || null) : current));
  };

  const saveQuestions = async () => {
    // 1. Validation checks
    for (const question of questions) {
      const plainQuestionName = stripHtml(question.QuestionName);
      if (!plainQuestionName) {
        CustomToast("fail", "Please enter question name.");
        return false;
      }

      if (["Multiple Choice", "Check Boxes", "Dropdown", "Rank"].includes(question.OptionType)) {
        if (question.Options.length < 2) {
          CustomToast("fail", `"${plainQuestionName || "Untitled Question"}" must have at least 2 options.`);
          return false;
        }
        if (question.Options.some((option) => !option.optionName.trim())) {
          CustomToast("fail", `"${plainQuestionName || "Untitled Question"}" contains empty options.`);
          return false;
        }
      }
      if (question.OptionType === "Rank") {
        const optionCount = question.Options.length;
        const ranks = question.Options.map((option) => Number(option.rank));

        const hasInvalidRank = ranks.some(
          (rank) => !Number.isInteger(rank) || rank < 1 || rank > optionCount,
        );
        if (hasInvalidRank) {
          CustomToast(
            "fail",
            `${describeQuestionScope(question, questions)} has an invalid rank — each option needs a whole number between 1 and ${optionCount}.`,
          );
          return false;
        }

        const uniqueRanks = new Set(ranks);
        if (uniqueRanks.size !== optionCount) {
          CustomToast(
            "fail",
            `${describeQuestionScope(question, questions)} has duplicate ranks — each option must occupy a unique position.`,
          );
          return false;
        }
      }
    }

    const caseIdLookup = {};
    const sectionAIdLookup = {};
    questions.forEach((question) => {
      if (question.Section === "Section B" && question.CaseType) {
        const key = `${question.ChannelType}::${question.CaseType}`;
        if (question.CaseID != null && caseIdLookup[key] == null) {
          caseIdLookup[key] = String(question.CaseID);
        }
      } else if (question.Section === "Section A") {
        if (question.CaseID != null && sectionAIdLookup[question.ChannelType] == null) {
          sectionAIdLookup[question.ChannelType] = String(question.CaseID);
        }
      }
    });

    const buildCaseFields = (question) => {
      if (question.Section === "Section B" && question.CaseType) {
        const key = `${question.ChannelType}::${question.CaseType}`;
        return {
          caseID: caseIdLookup[key] || null,
          caseTitle: question.CaseType,
          caseDescription: caseStudyContent[caseKey(question.ChannelType, question.CaseType)] || "",
        };
      }

      if (question.Section === "Section A") {
        // No case tabs in Section A — one instruction block per channel.
        // Reuses the same caseTitle/caseDescription fields already sent per question.
        return {
          caseID: sectionAIdLookup[question.ChannelType] || null,
          caseTitle: `${question.ChannelType} Instructions`,
          caseDescription: channelInstructions[question.ChannelType] || "",
        };
      }

      return { caseID: null, caseTitle: null, caseDescription: null };
    };

    // 3. Payload configuration
    const payload = {
      surveyUID,
      QuestionIDs: deletedQuestionIds.join(","),
      questionData: questions.map((question, index) => {
        const caseFields = buildCaseFields(question);

        return {
          questionID: String(question.QuestionID),
          uniqueKey: `${Date.now().toString().slice(-8)}${index}`,
          questionName: question.QuestionName,
          isRequired: question.IsRequired,
          isJumbleOptions: question.IsJumbleOptions,
          optionType: question.OptionType,
          status: question.Status,
          options: buildOptionsPayload(question),
          section: question.Section,
          experienceType: SECTIONS_WITHOUT_QUALIFICATION.includes(question.Section)
            ? "Both"
            : question.Qualification,
          channelID: channelIdMap[question.ChannelType],
          caseID: caseFields.caseID,
          caseTitle: caseFields.caseTitle,
          caseDescription: caseFields.caseDescription,
        };
      }),
    };

    const response = await createSurveyQuestions(payload);
    return {
      success: response.data.status?.toLowerCase() === "success",
      message: response.data.message,
    };
  };

  const buildOptionsPayload = (question) =>
    question.Options.map((option) => ({
      optionID: option.optionID ?? null,
      optionName: option.optionName,
      isDisqualifying: option.isDisqualifying ?? false,
      answerRank:
        question.OptionType === "Rank"
          ? Number(option.rank) || null
          : null,
    }));

  const handleSaveQuestions = async () => {
    try {
      const result = await saveQuestions();
      if (result.success) {
        CustomToast("success", result.message || "Draft saved successfully.");
        loadSurvey();
      } else {
        CustomToast("fail", result.message || "Failed to save draft.");
      }
    } catch (error) {
      CustomToast("fail", error.response?.data?.message || error.message || "Something went wrong.");
    }
  };

  const handleCancelEdit = () => {
    if (surveyUID) {
      loadSurvey();
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const section = result.source.droppableId;
    const sectionList = questions.filter((question) => {
      if (question.Section !== section || question.ChannelType !== selectedChannel) return false;
      if (section === "Section B") return question.CaseType === selectedCase;
      return true;
    });

    const draggedQuestion = sectionList[result.source.index];
    if (!draggedQuestion) return;

    const questionsWithoutDragged = questions.filter(
      (question) => question.QuestionID !== draggedQuestion.QuestionID,
    );

    const remainingSectionQuestions = questionsWithoutDragged.filter((question) => {
      if (question.Section !== section || question.ChannelType !== selectedChannel) return false;
      if (section === "Section B") return question.CaseType === selectedCase;
      return true;
    });

    let insertAt;
    if (result.destination.index < remainingSectionQuestions.length) {
      const targetQuestion = remainingSectionQuestions[result.destination.index];
      insertAt = questionsWithoutDragged.indexOf(targetQuestion);
    } else {
      const lastSectionQuestion =
        remainingSectionQuestions[remainingSectionQuestions.length - 1];
      insertAt = lastSectionQuestion
        ? questionsWithoutDragged.indexOf(lastSectionQuestion) + 1
        : questionsWithoutDragged.length;
    }

    questionsWithoutDragged.splice(insertAt, 0, draggedQuestion);
    setQuestions(questionsWithoutDragged);
  };

  const sectionCounts = sections.reduce((counts, section) => {
    counts[section] = questions.filter((question) => question.Section === section).length;
    return counts;
  }, {});

  const sectionQuestions = selectedSection
    ? questions.filter((question) => {
      if (question.Section !== selectedSection || question.ChannelType !== selectedChannel) return false;
      if (selectedSection === "Section B") return question.CaseType === selectedCase;
      return true;
    })
    : [];

  const allSectionRequired = sectionQuestions.length > 0 && sectionQuestions.every((question) => question.IsRequired);

  const jumbleEligibleQuestions = sectionQuestions.filter((question) =>
    ["Multiple Choice", "Check Boxes", "Dropdown", "Rank"].includes(question.OptionType)
  );
  const allSectionJumbled =
    jumbleEligibleQuestions.length > 0 && jumbleEligibleQuestions.every((question) => question.IsJumbleOptions);

  const handleToggleAllRequired = () => {
    if (sectionQuestions.length === 0) return;

    const nextRequired = !allSectionRequired;
    const idsInScope = new Set(sectionQuestions.map((question) => question.QuestionID));

    setQuestions((previousQuestions) =>
      previousQuestions.map((question) =>
        idsInScope.has(question.QuestionID)
          ? { ...question, IsRequired: nextRequired }
          : question
      )
    );

    const scopeLabel =
      selectedSection === "Section B" && selectedCase
        ? `${selectedSection} · ${selectedChannel} · ${selectedCase}`
        : `${selectedSection} · ${selectedChannel}`;
CustomToast(
      "success",
      `All questions in ${scopeLabel} marked ${nextRequired ? "required" : "optional"}.`
    );
  };

  const handleToggleAllJumble = () => {
    if (jumbleEligibleQuestions.length === 0) return;

    const nextJumbled = !allSectionJumbled;
    const idsInScope = new Set(jumbleEligibleQuestions.map((question) => question.QuestionID));

    setQuestions((previousQuestions) =>
      previousQuestions.map((question) =>
        idsInScope.has(question.QuestionID)
          ? { ...question, IsJumbleOptions: nextJumbled }
          : question
      )
    );

    const scopeLabel =
      selectedSection === "Section B" && selectedCase
        ? `${selectedSection} · ${selectedChannel} · ${selectedCase}`
        : `${selectedSection} · ${selectedChannel}`;

    CustomToast(
      "success",
      `All eligible questions in ${scopeLabel} marked ${nextJumbled ? "jumbled" : "not jumbled"}.`
    );
  };

   const allChannels = channelNames;

  const handleAddChannel = (channel) => {
    setVisibleChannels((previous) =>
      previous.includes(channel) ? previous : [...previous, channel]
    );
    setSelectedChannel(channel);
    setChannelPickerOpen(false);
  };

  const handleRemoveChannel = (channel) => {
    const questionsInChannel = questions.filter(
      (question) => question.ChannelType === channel
    ).length;

    if (questionsInChannel > 0) {
      CustomToast(
        "fail",
        `"${channel}" still has ${questionsInChannel} question(s). Delete them before removing this channel.`
      );
      return;
    }

    const remaining = visibleChannels.filter((name) => name !== channel);
    setVisibleChannels(remaining);
    if (selectedChannel === channel) {
      setSelectedChannel(remaining[0] || null);
    }
  };

  const hasChannelChanges =
    visibleChannels.length !== savedChannels.length ||
    visibleChannels.some((name) => !savedChannels.includes(name)) ||
    savedChannels.some((name) => !visibleChannels.includes(name));

  const handleCancelChannels = () => {
    setVisibleChannels(savedChannels);
    setSelectedChannel(savedChannels[0] || null);
    setChannelPickerOpen(false);
  };

  const handleSaveChannels = async () => {
    if (!surveyUID) {
      CustomToast("fail", "Save the survey first before assigning channels.");
      return;
    }

    // Channels the user removed since the last save need an explicit
    // isActive: 0 call — dropping them from visibleChannels alone never
    // tells the backend to deactivate them.
    const removedChannels = savedChannels.filter((name) => !visibleChannels.includes(name));
    const activeChannels = visibleChannels;

    const blockedChannel = removedChannels.find((channel) =>
      questions.some((question) => question.ChannelType === channel)
    );
    if (blockedChannel) {
      const questionCount = questions.filter(
        (question) => question.ChannelType === blockedChannel
      ).length;
      CustomToast(
        "fail",   
        `"${blockedChannel}" still has ${questionCount} question(s). Delete them before removing this channel.`
      );
      return;
    }

    const removedChannelIDs = removedChannels
      .map((name) => channelIdMap[name])
      .filter((id) => id != null);
    const activeChannelIDs = activeChannels
      .map((name) => channelIdMap[name])
      .filter((id) => id != null);

    setChannelSaving(true);
    let removeResponse = null;
    let addResponse = null;
    try {
      if (removedChannelIDs.length > 0) {
        removeResponse = await assignChannelToAssessment({
          assessmentUID: surveyUID,
          channelIDs: removedChannelIDs,
          isActive: 0,
        });
        if (removeResponse.data.status?.toLowerCase() !== "success") {
          CustomToast("fail", removeResponse.data.message || "Failed to remove some channels.");
          return;
        }
      }

      if (activeChannelIDs.length > 0) {
        addResponse = await assignChannelToAssessment({
          assessmentUID: surveyUID,
          channelIDs: activeChannelIDs,
          isActive: 1,
        });
        if (addResponse.data.status?.toLowerCase() !== "success") {
          CustomToast("fail", addResponse.data.message || "Failed to save active channels.");
          return;
        }
      }

      setSavedChannels(visibleChannels);
      // setChannelRemoveMode(false);
      CustomToast("success", addResponse?.data?.message || removeResponse?.data?.message || "Channels saved.");
    } catch (error) {
      CustomToast("fail", error.response?.data?.message || error.message || "Failed to save channels.");
    } finally {
      setChannelSaving(false);
    }
  };

  const activeCaseList = channelCases[selectedChannel] || [];
  const showCaseStudy = SECTIONS_WITH_CASE_STUDY.includes(selectedSection) && Boolean(selectedCase);

  return (
    <div className="min-h-screen bg-[#f7f8fa]">
      <div className="mx-auto max-w-[1440px] py-2">
        <SurveyNavTabs
          active="questions"
          surveyUID={surveyUID}
          surveyName={surveyName}
          surveyDescription={surveyDescription}
          viewOnly={viewOnly}
          surveyStatus={survey?.surveyStatus || location.state?.surveyStatus}
        />
      </div>
      <div className="mx-auto flex max-w-[1440px] gap-3 pb-6">
        <aside className="sticky top-[72px] flex h-[calc(100vh-96px)] w-[320px] flex-shrink-0 flex-col gap-4 overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          {loading || channelsLoading ? <SidebarSkeleton /> : (
            <>
              {survey && (
                <div className="rounded-xl bg-gradient-to-br from-[#7f35b2]/10 to-[#7f35b2]/5 p-4">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <h2 className="text-base font-semibold leading-tight text-gray-900">{survey.surveyName}</h2>
                    <span className="flex-shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                      {survey.surveyStatus === "Incomplete" ? "Draft" : survey.surveyStatus}
                    </span>
                  </div>
                  <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                  <div
                    className="description-rich-content text-xs text-gray-600 line-clamp-5"
                    title={stripHtml(survey.surveyDescription)}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(survey.surveyDescription) }}
                  />
                </div>
              )}

              <div className="flex-1 overflow-y-auto pr-1">
                <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Sections</div>
                <DragDropContext onDragEnd={onDragEnd}>
                  <div className="space-y-2">
                    {sections.map((section) => {
                      const isSelected = selectedSection === section;
                      const isExpanded = expandedSection === section;
                      const sectionList = (!selectedChannel || !visibleChannels.includes(selectedChannel))
                        ? []
                        : questions.filter((question) => {
                          if (question.Section !== section || question.ChannelType !== selectedChannel) return false;
                          if (section === "Section B") return question.CaseType === selectedCase;
                          return true;
                        });

                      return (
                        <div
                          key={section}
                          className={`rounded-xl border transition-colors ${isSelected ? "border-[#7f35b2]/30 bg-[#7f35b2]/5" : "border-transparent bg-gray-50"
                            }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleSection(section)}
                            aria-expanded={isExpanded}
                            className="flex w-full items-center justify-between px-3 py-2.5"
                          >
                            <div className="flex items-center gap-2">
                              <Layers size={14} className={isSelected ? "text-[#7f35b2]" : "text-gray-400"} />
                              <span className={`text-sm font-medium ${isSelected ? "text-[#7f35b2]" : "text-gray-700"}`}>
                                {section}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isSelected ? "bg-[#7f35b2] text-white" : "bg-gray-200 text-gray-600"
                                }`}>
                                {sectionCounts[section] || 0}
                              </span>
                              <motion.span animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="text-gray-400">
                                <ChevronDown size={14} />
                              </motion.span>
                            </div>
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded && (
                              <motion.div
                                key="content"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                className="max-h-[270px] overflow-y-auto"
                              >
                                <Droppable droppableId={section}>
                                  {(droppableProvided) => (
                                    <div className="px-2 pb-2">
                                      <div
                                        ref={droppableProvided.innerRef}
                                        {...droppableProvided.droppableProps}
                                        className="space-y-1"
                                      >
                                        {sectionList.map((question, index) => (
                                          <Draggable key={question.QuestionID} draggableId={String(question.QuestionID)} index={index} isDragDisabled={viewOnly}>
                                            {(draggableProvided, snapshot) => (
                                              <div
                                                ref={draggableProvided.innerRef}
                                                {...draggableProvided.draggableProps}
                                                {...draggableProvided.dragHandleProps}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() =>
                                                  handleSidebarItemClick(section, question.QuestionID)
                                                }
                                                className={`group flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white ${snapshot.isDragging ? "bg-white shadow-lg" : ""
                                                  }`}
                                              >
                                                {!viewOnly && <GripVertical size={12} className="flex-shrink-0 text-gray-300 opacity-0 group-hover:opacity-100" />}
                                                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-[10px] font-semibold text-gray-500">
                                                  {index + 1}
                                                </span>
                                                <span className="line-clamp-1 flex-1 text-xs text-gray-600">
                                                  {stripHtml(question.QuestionName) || "Untitled Question"}
                                                </span>
                                              </div>
                                            )}
                                          </Draggable>
                                        ))}
                                        {droppableProvided.placeholder}
                                      </div>
                                      {!viewOnly && (
                                        <button
                                          type="button"
                                          onClick={() => addQuestion(section, selectedChannel)}
                                          disabled={
                                            !selectedChannel ||
                                            !visibleChannels.includes(selectedChannel) ||
                                            (section === "Section B" && (channelCases[selectedChannel] || []).length === 0)
                                          }
                                          title={
                                            !selectedChannel || !visibleChannels.includes(selectedChannel)
                                              ? "Add a channel first before adding questions."
                                              : section === "Section B" && (channelCases[selectedChannel] || []).length === 0
                                              ? "Add a case first before adding questions to Section B."
                                              : undefined
                                          }
                                          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-white py-2 text-xs font-medium text-[#7f35b2] ring-1 ring-inset ring-[#7f35b2]/20 transition hover:bg-[#7f35b2]/10 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                                        >
                                          <PlusCircle size={13} />
                                          Add to {section}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </Droppable>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </DragDropContext>
              </div>
            </>
          )}
        </aside>

        {/* MAIN QUESTION WORKSPACE */}
        <main className="min-w-0 flex-1 space-y-3">
          {/* Header Div */}
          {loading || channelsLoading ? <ContentSkeleton /> : (
            <>
              <div className="flex items-start justify-between gap-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <div>
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#7f35b2]">
                    <span>{selectedSection}</span>
                    <span>·</span>
                    <span className="text-gray-600">{selectedChannel}</span>
                    {selectedSection === "Section B" && selectedCase && (
                      <>
                        <span>·</span>
                        <span className="text-amber-600">{selectedCase}</span>
                      </>
                    )}
                  </div>
                  <h1 className="mt-1 text-2xl font-bold text-gray-900">Questions</h1>
                  <p className="mt-1 text-sm text-gray-500">
                    {sectionQuestions.length} question{sectionQuestions.length === 1 ? "" : "s"} in this{" "}
                    {selectedSection === "Section B" ? "case" : "channel"}. Drag, duplicate, or add new below.
                  </p>
                </div>
                {!viewOnly &&
                  (
                    <div className="flex flex-shrink-0 flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleToggleAllJumble}
                          disabled={jumbleEligibleQuestions.length === 0}
                          title={
                            jumbleEligibleQuestions.length === 0
                              ? "No option-based questions in this scope yet."
                              : allSectionJumbled
                                ? "Turn off jumbled options for every eligible question in this scope"
                                : "Jumble options for every eligible question in this scope"
                          }
                          className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700 shadow-sm transition hover:border-[#7f35b2] hover:text-[#7f35b2] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:text-gray-700"
                        >
                          {allSectionJumbled ? <ToggleRight size={15} className="text-[#7f35b2]" /> : <ToggleLeft size={15} />}
                          {allSectionJumbled ? "Unmark all jumble" : "Mark all jumble"}
                        </button>
                        <button
                          type="button"
                          onClick={handleToggleAllRequired}
                          disabled={sectionQuestions.length === 0}
                          title={
                            sectionQuestions.length === 0
                              ? "No questions in this scope yet."
                              : allSectionRequired
                                ? "Mark every question in this scope as optional"
                                : "Mark every question in this scope as required"
                          }
                          className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700 shadow-sm transition hover:border-[#7f35b2] hover:text-[#7f35b2] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:text-gray-700"
                        >
                          {allSectionRequired ? <ToggleRight size={15} className="text-[#7f35b2]" /> : <ToggleLeft size={15} />}
                          {allSectionRequired ? "Mark all optional" : "Mark all required"}
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => addQuestion()}
                        disabled={
                          !selectedChannel ||
                          !visibleChannels.includes(selectedChannel) ||
                          (selectedSection === "Section B" && activeCaseList.length === 0)
                        }
                        title={
                          !selectedChannel || !visibleChannels.includes(selectedChannel)
                            ? "Add a channel first before adding questions."
                            : selectedSection === "Section B" && activeCaseList.length === 0
                            ? "Add a case first before adding questions to Section B."
                            : undefined
                        }
                        className="flex items-center gap-2 rounded-full bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#7f35b2]"
                      >
                        <PlusCircle size={16} />
                        New question
                      </button>
                    </div>
                  )
                }
              </div>

              {/* Channel Toggle Bar */}
              <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
                {!viewOnly && (
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setChannelPickerOpen((open) => !open)}
                      className="flex items-center gap-1.5 rounded-xl border border-dashed border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-400 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                    >
                      <PlusCircle size={14} />
                      Manage Channel
                    </button>

                    {channelPickerOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setChannelPickerOpen(false)}
                        />

                        <div className="absolute left-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
                          {allChannels.map((channel) => {
                            const isAlreadyAdded = visibleChannels.includes(channel);

                            return (
                              <button
                                key={channel}
                                type="button"
                                onClick={() => handleAddChannel(channel)}
                                className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition ${
                                  isAlreadyAdded
                                    ? "text-gray-400 hover:bg-gray-50"
                                    : "text-gray-700 hover:bg-gray-50 hover:text-[#7f35b2]"
                                }`}
                              >
                                <span>{channel}</span>

                                {isAlreadyAdded && (
                                  <span className="text-xs text-gray-400">
                                    (Added)
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {visibleChannels.map((channel) => {
                  const isActive = selectedChannel === channel;
                  const channelQuestionCount = questions.filter(
                    (q) => q.Section === selectedSection && q.ChannelType === channel
                  ).length;

                  return (
                    <div
                      key={channel}
                      className={`group flex items-center gap-1.5 rounded-xl pl-4 pr-2 py-2 text-sm font-medium transition-all ${isActive
                        ? "bg-[#7f35b2] text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedChannel(channel)}
                        className="flex items-center gap-2"
                      >
                        <span>{channel}</span>
                        <span
                          className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isActive
                            ? "bg-white/20 text-white"
                            : "bg-gray-100 text-gray-500"
                            }`}
                        >
                          {channelQuestionCount}
                        </span>
                      </button>

                      {!viewOnly && (
                        <>
                          {/* Vertical Divider */}
                          <div
                            className={`mx-1 h-5 w-px ${
                              isActive ? "bg-white/30" : "bg-gray-300"
                            }`}
                          />

                          <button
                            type="button"
                            onClick={() => handleRemoveChannel(channel)}
                            disabled={channelSaving}
                            title={`Remove ${channel}`}
                            className={`flex-shrink-0 rounded-full p-0.5 transition disabled:cursor-not-allowed disabled:opacity-40 ${
                              isActive
                                ? "text-red-300 hover:bg-white/20 hover:text-red-400"
                                : "text-red-500 hover:bg-red-50 hover:text-red-600"
                            }`}
                          >
                            <X size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Stable Cancel & Save Buttons */}
                {!viewOnly && hasChannelChanges && (
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCancelChannels}
                      disabled={channelSaving}
                      className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleSaveChannels}
                      disabled={channelSaving}
                      className="rounded-xl bg-[#7f35b2] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {channelSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                )}
              </div>

              {!selectedChannel || !visibleChannels.includes(selectedChannel) ? (
                <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
                  No channel selected yet.
                </div>
              ) : (
                <>
              {selectedSection === "Section A" && (
                <section className="rounded-2xl border border-amber-200/60 bg-gradient-to-br from-amber-50/60 to-white p-6 shadow-sm">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                      <FileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Instructions — <span className="text-amber-700">{selectedChannel}</span>
                      </h3>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Responders read this before answering {selectedChannel} questions in Section A.
                      </p>
                    </div>
                  </div>
                  <RichTextEditor
                    key={`sectionA-${selectedChannel}`}
                    initialContent={channelInstructions[selectedChannel] || ""}
                    placeholder={`Write instructions for ${selectedChannel}...`}
                    editable={!viewOnly}
                    onChange={viewOnly ? undefined : (html) =>
                      setChannelInstructions((previous) => ({ ...previous, [selectedChannel]: html }))
                    }
                  />
                </section>
              )}

              {selectedSection === "Section B" && (
                <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      <FileText size={13} className="text-amber-500" />
                      <span>Cases for {selectedChannel}</span>
                    </div>
                    <span className="text-[11px] text-gray-400">Scoped to {selectedChannel} channel</span>
                  </div>
                  <CaseTabs
                    value={selectedCase}
                    options={activeCaseList}
                    onSelect={setSelectedCase}
                    onAdd={addCase}
                    onRename={renameCase}
                    onRemove={removeCase}
                    viewOnly={viewOnly}
                  />
                </div>
              )}

              {showCaseStudy && (
                <section className="rounded-2xl border border-amber-200/60 bg-gradient-to-br from-amber-50/60 to-white p-6 shadow-sm">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                      <FileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Case Study Content — <span className="text-amber-700">{selectedCase}</span>
                      </h3>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Responders read this passage before answering the {selectedCase} questions under {selectedChannel}.
                      </p>
                    </div>
                  </div>
                  <RichTextEditor
                    key={caseKey(selectedChannel, selectedCase)}
                    initialContent={caseStudyContent[caseKey(selectedChannel, selectedCase)] || ""}
                    placeholder={`Write the ${selectedCase} passage here...`}
                    editable={!viewOnly}
                    onChange={viewOnly ? undefined : (html) =>
                      setCaseStudyContent((previous) => ({ ...previous, [caseKey(selectedChannel, selectedCase)]: html, }))
                    }
                  />
                </section>
              )}

              {sectionQuestions.map((question, index) => {
                const showQualification = !SECTIONS_WITHOUT_QUALIFICATION.includes(question.Section);

                return (
                  <article
                    key={question.QuestionID}
                    id={`question-${question.QuestionID}`}
                    ref={(element) => {
                      if (element) {
                        questionRefs.current[question.QuestionID] = element;
                      }
                    }}
                    tabIndex={-1}
                    className="scroll-mt-24 rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/40"
                  >
                    <div className="flex items-center justify-between border-b border-gray-50 px-6 py-3">
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-400">
                        <span className="rounded-full bg-[#7f35b2]/10 px-2.5 py-1 text-xs font-semibold text-[#7f35b2]">
                          Q{index + 1}
                        </span>
                        <span>
                          {question.Section} · {question.ChannelType} · {question.OptionType} {viewOnly && showQualification ? ` · ${question.Qualification}` : ""}
                        </span>
                        {viewOnly && question.IsRequired && (
                          <span className="ml-0.5 text-red-500 text-xl leading-none" title="Required">*</span>
                        )}
                        {viewOnly && question.IsJumbleOptions && (
                          <span
                            className="ml-1 flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-600"
                            title="Options are shown to candidates in randomized order"
                          >
                            <Shuffle size={11} />
                            Jumbled
                          </span>
                        )}
                      </div>
                      {!viewOnly && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => requestDuplicate(question)}
                            className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                            title="Duplicate"
                          >
                            <Copy size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteQuestion(question.QuestionID)}
                            className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-500"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="px-6 pb-2 pt-4">
                      <RichTextEditor
                        initialContent={question.QuestionName}
                        placeholder="Enter your question here..."
                        minHeight={100}
                        editable={!viewOnly}
                        onChange={viewOnly ? undefined : (html) => updateQuestion(question.QuestionID, "QuestionName", html)}
                      />
                    </div>

                    <div className="space-y-6 px-6 pb-6">
                      {!viewOnly && (
                        <div className={`grid gap-4 sm:grid-cols-2 ${showQualification ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                              <Layers size={12} className="text-[#7f35b2]" /> Section
                            </div>
                            <select
                              value={question.Section}
                              onChange={(event) =>
                                requestSectionChange(question.QuestionID, event.target.value)
                              }
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                            >
                              {sections.map((section) => (
                                <option key={section}>{section}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                              <ListChecks size={12} className="text-violet-500" /> Type
                            </div>
                            <select
                              value={question.OptionType}
                              onChange={(event) =>
                                updateQuestion(question.QuestionID, "OptionType", event.target.value)
                              }
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                            >
                              {questionTypes.map((type) => (
                                <option key={type}>{type}</option>
                              ))}
                            </select>
                          </div>

                          {showQualification && (
                            <div>
                              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                <User size={12} className="text-[#7f35b2]" /> Qualification
                              </div>
                              <select
                                value={question.Qualification}
                                onChange={(event) =>
                                  updateQuestion(question.QuestionID, "Qualification", event.target.value)
                                }
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                              >
                                {QUALIFICATION_OPTIONS.map((option) => (
                                  <option key={option}>{option}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      )}

                      {(question.OptionType === "Short Answer" || question.OptionType === "Paragraph") && (
                        <div className="rounded-2xl border border-gray-100 bg-slate-50 p-5">
                          {question.OptionType === "Short Answer" ? (
                            <input readOnly={viewOnly} disabled placeholder="Short answer" className="w-full rounded-lg border bg-white px-3 py-2 text-sm" />
                          ) : (
                            <textarea disabled rows={4} placeholder="Paragraph" className="w-full rounded-lg border bg-white px-3 py-2 text-sm" />
                          )}
                        </div>
                      )}

                      {["Multiple Choice", "Check Boxes", "Dropdown", "Rank"].includes(question.OptionType) && (
                        <div>
                          <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">Options</h3>
                            <span className="text-xs text-gray-400">
                              {question.Options.length} option{question.Options.length === 1 ? "" : "s"}
                            </span>
                          </div>

                          {question.OptionType === "Rank" && (
                            <p className="mb-2 text-xs text-gray-400">
                              Set the correct position (1, 2, 3…) each option should be dragged to in the correct sequence.
                            </p>
                          )}

                          <div className="space-y-2">
                            {question.Options.map((option, optionIndex) => (
                              <div
                                key={optionIndex}
                                className={`group flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${viewOnly
                                  ? "bg-gray-50/50"
                                  : "border border-gray-200 bg-white focus-within:border-[#7f35b2]"
                                  }`}
                              >
                                <OptionIndicator optionType={question.OptionType} index={optionIndex} />
                                <input
                                  readOnly={viewOnly}
                                  value={option.optionName}
                                  onChange={(event) => updateOption(question.QuestionID, optionIndex, event.target.value)}
                                  placeholder="Add option"
                                  className="flex-1 bg-transparent text-sm outline-none"
                                />
                                {question.OptionType === "Rank" && (
                                  <input
                                    disabled={viewOnly}
                                    type="number"
                                    min={1}
                                    value={option.rank ?? ""}
                                    onChange={(event) =>
                                      updateOptionRank(question.QuestionID, optionIndex, event.target.value)
                                    }
                                    placeholder="Order"
                                    title="Correct position in the sequence"
                                    className="w-20 flex-shrink-0 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                                  />
                                )}
                                {!viewOnly && (
                                  <button
                                    type="button"
                                    onClick={() => deleteOption(question.QuestionID, optionIndex)}
                                    className="flex-shrink-0 text-gray-300 opacity-0 transition group-hover:opacity-100 hover:text-red-500"
                                    title="Remove"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>

                          {!viewOnly && (<div className="mt-3 flex items-center gap-5">
                            <button type="button" onClick={() => addOption(question.QuestionID)}
                              className="flex items-center gap-1.5 text-sm font-medium text-[#7f35b2] transition hover:text-[#48086f]"
                            >
                              <PlusCircle size={14} />
                              Add option
                            </button>
                            <button type="button" onClick={() => addOtherOption(question.QuestionID)}
                              className="flex items-center gap-1.5 text-sm font-medium text-gray-500 transition hover:text-gray-700"
                            > <Check size={14} />
                              Add &quot;Other&quot;
                            </button>
                          </div>)}
                        </div>
                      )}

                      {!viewOnly && (
                        <div className="flex items-center justify-end gap-6 border-t border-gray-100 pt-4">
                          {["Multiple Choice", "Check Boxes", "Dropdown", "Rank"].includes(question.OptionType) && (
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-700">Jumble options</span>
                              <button type="button" onClick={() => updateQuestion(question.QuestionID, "IsJumbleOptions", !question.IsJumbleOptions)}
                                aria-pressed={question.IsJumbleOptions}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${question.IsJumbleOptions ? "bg-[#7f35b2]" : "bg-gray-300"
                                  }`}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${question.IsJumbleOptions ? "translate-x-5" : "translate-x-1"
                                    }`}
                                />
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">Required</span>
                            <button type="button" onClick={() => !viewOnly && updateQuestion(question.QuestionID, "IsRequired", !question.IsRequired)}
                              aria-pressed={question.IsRequired}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${question.IsRequired ? "bg-[#7f35b2]" : "bg-gray-300"
                                }${viewOnly ? "cursor-default" : ""}`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${question.IsRequired ? "translate-x-5" : "translate-x-1"
                                  }`}
                              />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}

              {sectionQuestions.length === 0 && (
                <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                  {selectedSection === "Section B" && !selectedCase
                    ? "No cases yet for this channel — add one above to start adding questions."
                    : selectedSection
                      ? `No questions in ${selectedSection} under ${selectedChannel}${selectedSection === "Section B" ? " (" + selectedCase + ")" : ""} yet. Add a new question from the sidebar.`
                      : "Select a section from the sidebar to view questions."}
                </div>
              )}

              </>
              )}

              {!viewOnly && (<div className="sticky bottom-6 z-10 ml-auto flex w-fit items-center gap-2 rounded-2xl border border-gray-100 bg-white/95 p-2 shadow-lg backdrop-blur">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  Cancel
                </button>
                 <button
                  type="button"
                  onClick={handleSaveQuestions}
                  disabled={loading}
                  className="rounded-xl bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Loading..." : "Save Questions"}
                </button>
              </div>)}
            </>
          )}
        </main>
      </div>

      <AnimatePresence>
        {placementModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={closePlacementModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.18 }}
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
            >
              <h3 className="text-base font-semibold text-gray-900">
                {placementModal.mode === "duplicate"
                  ? "Duplicate question"
                  : `Move to ${placementModal.toSection}`}
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                {placementModal.mode === "duplicate"
                  ? "Choose where the copy should land."
                  : placementModal.toSection === "Section B"
                    ? "Confirm which channel and case this question belongs to."
                    : "Confirm which channel this question belongs to."}
              </p>

              {placementModal.mode === "duplicate" && (
                <div className="mt-4">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Section
                  </div>
                  <select
                    value={placementModal.toSection}
                    onChange={(event) => {
                      const toSection = event.target.value;
                      const casesForChannel = channelCases[placementModal.channel] || [];
                      setPlacementModal((previous) => ({
                        ...previous,
                        toSection,
                        caseType:
                          toSection === "Section B" && casesForChannel.includes(previous.caseType)
                            ? previous.caseType
                            : null,
                        addingNewCase: toSection === "Section B" && casesForChannel.length === 0,
                        newCaseName: "",
                      }));
                    }}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                  >
                    {sections.map((section) => (
                      <option key={section}>{section}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mt-4">
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Channel
                </div>
                <select
                  value={placementModal.channel}
                  onChange={(event) => {
                    const channel = event.target.value;
                    const casesForChannel = channelCases[channel] || [];
                    setPlacementModal((previous) => ({
                      ...previous,
                      channel,
                      caseType: null,
                      addingNewCase:
                        previous.toSection === "Section B" && casesForChannel.length === 0,
                      newCaseName: "",
                    }));
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                >
                  {channelNames.map((channel) => (
                    <option key={channel}>{channel}</option>
                  ))}
                </select>
              </div>

              {placementModal.toSection === "Section B" && (
                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      Case
                    </span>
                    {!placementModal.addingNewCase && (
                      <button
                        type="button"
                        onClick={() =>
                          setPlacementModal((previous) => ({
                            ...previous,
                            addingNewCase: true,
                            caseType: null,
                          }))
                        }
                        className="text-[11px] font-medium text-[#7f35b2] hover:text-[#48086f]"
                      >
                        + New case
                      </button>
                    )}
                  </div>

                  {placementModal.addingNewCase ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={placementModal.newCaseName}
                        onChange={(event) =>
                          setPlacementModal((previous) => ({
                            ...previous,
                            newCaseName: event.target.value,
                          }))
                        }
                        placeholder="e.g. Case 1"
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                      />
                      {(channelCases[placementModal.channel] || []).length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setPlacementModal((previous) => ({
                              ...previous,
                              addingNewCase: false,
                              newCaseName: "",
                            }))
                          }
                          title="Pick an existing case instead"
                          className="flex-shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <select
                      value={placementModal.caseType || ""}
                      onChange={(event) =>
                        setPlacementModal((previous) => ({
                          ...previous,
                          caseType: event.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#7f35b2] focus:ring-2 focus:ring-[#7f35b2]/20"
                    >
                      <option value="" disabled>
                        Select a case
                      </option>
                      {(channelCases[placementModal.channel] || []).map((caseName) => (
                        <option key={caseName} value={caseName}>
                          {caseName}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closePlacementModal}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmPlacement}
                  className="rounded-xl bg-[#7f35b2] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#3bb290]"
                >
                  {placementModal.mode === "duplicate" ? "Create copy" : "Confirm move"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddQuestions;


